<script setup lang="ts">
import { SIDEBAR_IDS } from '@design-system/data/sidebarIds'
import type { ProductCardHorizontalProps } from '@design-system/components/UI/UIProductCardHorizontal.props'

/**
 * Product box of the fast checkout sidesheet, laid out like a minicart brand
 * group: quantity and edit act on the isolated PDP express order, and the edit
 * opens a nested quick buy that swaps the express line item.
 */
const props = defineProps<{
  card: ProductCardHorizontalProps
  /** False until the express order holds the line item: no quantity nor edit. */
  isEditable: boolean
}>()

const { getBrandName } = useBrandsSettings()
const { updateQuantity } = useExpressOrder()
const { openDialog } = useDialog(SIDEBAR_IDS.uiQuickBuyPopupSlideFastCheckout)

const isQuantityUpdating = ref(false)
const itemsQtyUpdating = computed(() => ({
  [props.card.id]: isQuantityUpdating.value,
}))

const changeQuantity = async (delta: number) => {
  if (!props.card.quantity || isQuantityUpdating.value) return
  isQuantityUpdating.value = true
  try {
    await updateQuantity(props.card.id, props.card.quantity + delta)
  } catch (error) {
    console.error('Failed to update PDP express order quantity', error)
  } finally {
    isQuantityUpdating.value = false
  }
}

const handleIncreaseQuantity = () => changeQuantity(1)
const handleDecreaseQuantity = () => changeQuantity(-1)

const quickBuyProduct = ref<ProductCardHorizontalProps>()
const handleEditClick = async () => {
  quickBuyProduct.value = props.card
  await nextTick()
  openDialog(SIDEBAR_IDS.uiQuickBuyPopupSlideFastCheckout)
}
</script>

<template>
  <div class="bg-primitives-off-white px-6 py-8">
    <h3>
      <component
        :is="logoMap[card.metadata]"
        class="mb-8 h-5 w-fit"
        :title="getBrandName(card.metadata)"
      />
    </h3>
    <div class="flex flex-col">
      <UIProductCardHorizontal
        v-bind="card"
        :is-cart="isEditable"
        disable-gallery
        :enable-quick-buy="false"
        :is-price-visible="false"
        :items-qty-updating="itemsQtyUpdating"
        @on-increase-qty="handleIncreaseQuantity"
        @on-decrease-qty="handleDecreaseQuantity"
      >
        <template #quantity-row-end>
          <button
            v-if="card.canEditVariant"
            :aria-label="`${$ts('aria-labels.cartPage.openQuickbuy')} ${card.info.name}`"
            aria-haspopup="dialog"
            @click="handleEditClick"
          >
            <DefaultIconsEdit aria-hidden="true" />
          </button>
        </template>
      </UIProductCardHorizontal>
    </div>
  </div>

  <UIQuickBuyPopUpSlide
    v-if="quickBuyProduct"
    :id="SIDEBAR_IDS.uiQuickBuyPopupSlideFastCheckout"
    :key="quickBuyProduct.skuCode"
    :product="quickBuyProduct"
    target="express"
    is-item-replaced
    is-nested
    has-back-button
    :has-close-button="false"
  />
</template>
